import React from 'react';

interface InvoiceEmailTemplateProps {
  invoice: any;
  companyName?: string;
  companyAddress?: string;
  companyCif?: string;
  companyEmail?: string;
  companyPhone?: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const InvoiceEmailTemplate: React.FC<InvoiceEmailTemplateProps> = ({
  invoice,
  companyName = 'Marcial Pons',
  companyAddress = '',
  companyCif = '',
  companyEmail = '',
  companyPhone = '',
}) => {
  const client = invoice.client || {};
  const lines = invoice.lines || [];
  const subtotal = invoice.subtotal || 0;
  const vatAmount = invoice.vatAmount || 0;
  const totalFactura = invoice.totalFactura || 0;
  const vatBreakdown = invoice.vatBreakdown || {};

  return (
    <div style={{
      fontFamily: 'Arial, Helvetica, sans-serif',
      maxWidth: '600px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      color: '#333',
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a2e',
        color: '#fff',
        padding: '30px',
        textAlign: 'center',
        borderRadius: '8px 8px 0 0',
      }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700 }}>
          {companyName}
        </h1>
        <p style={{ margin: '10px 0 0', fontSize: '14px', opacity: 0.9 }}>
          Factura {invoice.invoiceNumber || invoice.invoiceId}
        </p>
      </div>

      {/* Body */}
      <div style={{
        backgroundColor: '#fff',
        padding: '30px',
        border: '1px solid #e0e0e0',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
      }}>
        {/* Invoice meta */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
          <tr>
            <td style={{ padding: '8px 0', fontWeight: 600, width: '120px' }}>Fecha:</td>
            <td style={{ padding: '8px 0' }}>{formatDate(invoice.date)}</td>
          </tr>
          <tr>
            <td style={{ padding: '8px 0', fontWeight: 600 }}>Número:</td>
            <td style={{ padding: '8px 0' }}>{invoice.invoiceNumber || invoice.invoiceId}</td>
          </tr>
          <tr>
            <td style={{ padding: '8px 0', fontWeight: 600 }}>Estado:</td>
            <td style={{ padding: '8px 0' }}>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
              }}>
                Emitida
              </span>
            </td>
          </tr>
        </table>

        {/* Company & Client info */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px',
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#666', textTransform: 'uppercase' }}>
              Emisor
            </h3>
            <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>{companyName}</strong></p>
            {companyAddress && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{companyAddress}</p>}
            {companyCif && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>CIF: {companyCif}</p>}
            {companyEmail && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{companyEmail}</p>}
            {companyPhone && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{companyPhone}</p>}
          </div>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '14px', color: '#666', textTransform: 'uppercase' }}>
              Cliente
            </h3>
            <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>{client.name || 'Cliente'}</strong></p>
            {client.contactPerson && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{client.contactPerson}</p>}
            {client.address && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{client.address}</p>}
            {client.cif && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>CIF: {client.cif}</p>}
            {client.email && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{client.email}</p>}
            {client.phone && <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{client.phone}</p>}
          </div>
        </div>

        {/* Invoice lines table */}
        <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#333' }}>Detalle de la factura</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Descripción</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', width: '80px' }}>Cant.</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', width: '100px' }}>Precio</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', width: '60px' }}>IVA</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', width: '100px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line: any, idx: number) => (
                <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>{line.name || line.description || 'Artículo'}</strong>
                    {line.sku && <span style={{ fontSize: '11px', color: '#999' }}>SKU: {line.sku}</span>}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{line.quantity}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>{formatCurrency(line.price || 0)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{line.vat || line.tax_rate || 0}%</td>
                  <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(line.base || (line.quantity * line.price) || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ marginTop: '24px', paddingLeft: 'auto', width: '300px', marginLeft: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
            <span>Base imponible:</span>
            <strong>{formatCurrency(subtotal)}</strong>
          </div>
          {Object.entries(vatBreakdown).map(([vat, data]: [string, any]) => (
            <div key={vat} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
              <span>IVA {vat}%:</span>
              <strong>{formatCurrency(data.amount || 0)}</strong>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #1a1a2e', fontSize: '16px', fontWeight: 700 }}>
            <span>TOTAL:</span>
            <span style={{ color: '#1a1a2e' }}>{formatCurrency(totalFactura)}</span>
          </div>
        </div>

        {/* Payment info */}
        {invoice.paymentTerms && (
          <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#666', textTransform: 'uppercase' }}>
              Forma de pago
            </h4>
            <p style={{ margin: 0, fontSize: '13px' }}>{invoice.paymentTerms}</p>
          </div>
        )}

        {/* Bank account */}
        {invoice.bankAccount && (
          <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#666', textTransform: 'uppercase' }}>
              Cuenta bancaria
            </h4>
            <p style={{ margin: 0, fontSize: '13px', fontFamily: 'monospace' }}>{invoice.bankAccount}</p>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #eee', textAlign: 'center', fontSize: '12px', color: '#999' }}>
          <p style={{ margin: '4px 0' }}>Gracias por su confianza.</p>
          <p style={{ margin: '4px 0' }}>{companyName} • {companyEmail} • {companyPhone}</p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceEmailTemplate;
